import { type Href, usePathname, useRouter } from 'expo-router'
import type { RouteName } from '@/lib/routes'
import { routes } from '@/lib/routes'
import { Button } from '@/ui/Button'

interface ParentLinkButtonProps {
    fallbackKey: RouteName
    orgSlug: string
}

function extractParamsFromPath(
    currentPath: string,
    fallbackKey: RouteName,
    orgSlug: string
): Record<string, string> {
    const params: Record<string, string> = {}
    const pathParts = currentPath.split('/').filter(Boolean)
    const routeParts = fallbackKey.split('/').filter(Boolean)

    const orgIndex = pathParts.indexOf(orgSlug)
    if (orgIndex === -1) return params

    const relevantPathParts = pathParts.slice(orgIndex + 1)

    for (let i = 0; i < routeParts.length && i < relevantPathParts.length; i++) {
        const routePart = routeParts[i]
        const pathPart = relevantPathParts[i]
        const match = routePart.match(/^\[([^\]]+)\]$/)
        if (match) {
            params[match[1]] = pathPart
        }
    }

    return params
}

function buildFallbackUrl(fallbackKey: RouteName, orgSlug: string, currentPath: string): Href {
    const params = extractParamsFromPath(currentPath, fallbackKey, orgSlug)
    let url = `/${orgSlug}/${fallbackKey}`

    const paramMatches = fallbackKey.matchAll(/\[([^\]]+)\]/g)
    for (const match of paramMatches) {
        const paramName = match[1]
        const paramValue = params[paramName]
        if (paramValue) {
            url = url.replace(`[${paramName}]`, paramValue)
        }
    }

    if (url.endsWith('/index')) {
        url = url.slice(0, -6)
    }

    return url as Href
}

export function ParentLinkButton({ fallbackKey, orgSlug }: ParentLinkButtonProps) {
    const router = useRouter()
    const pathname = usePathname()

    const handlePress = () => {
        const fallbackUrl = buildFallbackUrl(fallbackKey, orgSlug, pathname)
        router.replace(fallbackUrl)
    }

    return (
        <Button
            title={routes[fallbackKey].title || 'Back'}
            icon="chevron-left"
            onPress={handlePress}
        />
    )
}
